'use client'

import { useTranslations } from 'next-intl'
import {
  type CSSProperties,
  isValidElement,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { CheckCircle, Info, WarningCircle, X, XCircle } from '@/components/icons'
import { Button } from '@/components/ui/button'
import type { DedupedKind } from '@/lib/toast/dedup'
import { cn } from '@/lib/utils'

/**
 * The visible toast surface.
 *
 * Sonner owns positioning, stacking, enter/exit, swipe-to-dismiss and the
 * dismiss timer; its own shell is neutralised in `sonner.tsx` so this card is
 * what the user actually sees. Two reasons the card lives here rather than
 * being styled onto sonner's root:
 *
 * 1. Sonner composes `transform: var(--y) …` on its root to carry the stack
 *    offset. Animating a scale there would overwrite it and snap the toast out
 *    of the stack mid-bump.
 * 2. Every toast surface in the app renders through this one component, so
 *    "they all match" holds by construction rather than by convention.
 */

/** Must match `--duration-fast`, which drives `.toast-card` in globals.css. */
export const TOAST_BUMP_MS = 150

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: WarningCircle,
  info: Info,
  message: Info,
} as const satisfies Record<DedupedKind, unknown>

const ICON_TONE: Record<DedupedKind, string> = {
  success: 'text-icon-success',
  error: 'text-icon-error',
  warning: 'text-icon-warning',
  info: 'text-icon-brand',
  message: 'text-icon-secondary',
}

export interface ToastCardProps {
  action?: ReactNode | ToastCardAction
  actionButtonStyle?: CSSProperties
  /** Times this exact toast has fired while live. The pill shows at >= 2. */
  count?: number
  description?: React.ReactNode
  icon?: React.ReactNode
  kind: DedupedKind
  onDismiss?: () => void
  open?: {
    label: string
    onClick: () => void
  }
  title: React.ReactNode
}

interface ToastCardAction {
  actionButtonStyle?: CSSProperties
  label: ReactNode
  onClick: (event: ReactMouseEvent<HTMLButtonElement>) => void
}

function isActionDefinition(action: ToastCardProps['action']): action is ToastCardAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    'label' in action &&
    action.label !== undefined &&
    'onClick' in action &&
    typeof action.onClick === 'function'
  )
}

export function ToastCard({
  action,
  actionButtonStyle,
  icon,
  kind,
  title,
  description,
  count,
  onDismiss,
  open,
}: ToastCardProps) {
  const t = useTranslations('Toast')
  const ref = useRef<HTMLDivElement>(null)
  const repeatCount = count ?? 1
  const seen = useRef(repeatCount)
  const Icon = ICONS[kind]
  const handleActionClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      if (!isActionDefinition(action)) {
        return
      }
      action.onClick(event)
      if (!event.defaultPrevented) {
        onDismiss?.()
      }
    },
    [action, onDismiss]
  )
  const handleDismissClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      onDismiss?.()
    },
    [onDismiss]
  )
  const handleDismissPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => event.stopPropagation(),
    []
  )

  // External sync, not derived state: a repeat has to re-drive a DOM
  // transition. Setting the attribute and clearing it one duration later is
  // what makes the transition run there-and-back.
  useEffect(() => {
    if (repeatCount === seen.current) {
      return
    }
    seen.current = repeatCount

    const el = ref.current
    if (el === null) {
      return
    }
    el.setAttribute('data-bumped', '1')
    const timer = setTimeout(() => el.setAttribute('data-bumped', '0'), TOAST_BUMP_MS)
    return () => clearTimeout(timer)
  }, [repeatCount])

  const content = (
    <>
      {icon ?? <Icon aria-hidden className={cn('mt-px size-4 shrink-0', ICON_TONE[kind])} />}

      <div className="min-w-0 flex-1">
        <p className="text-subtitle-sm text-foreground" data-testid="toast-title">
          {title}
        </p>
        {description ? (
          <p className="mt-0.5 text-subtitle-2xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </>
  )

  let actionContent: ReactNode = null
  if (isValidElement(action)) {
    actionContent = action
  } else if (isActionDefinition(action)) {
    actionContent = (
      <Button
        className="shrink-0"
        data-slot="toast-action"
        feedback="none"
        onClick={handleActionClick}
        size="sm"
        style={actionButtonStyle ?? action.actionButtonStyle}
        type="button"
        variant="secondary"
      >
        {action.label}
      </Button>
    )
  }

  return (
    <div
      className="toast-card flex w-full flex-col gap-2.5 rounded-lg border border-border bg-card p-3 surface-elevated"
      data-slot="toast-card"
      data-testid={`toast-${kind}`}
      ref={ref}
    >
      <div className="flex w-full items-start gap-2.5">
        {open ? (
          <button
            aria-label={open.label}
            className="-m-1 flex min-w-0 flex-1 items-start gap-2.5 rounded-md p-1 text-start transition-colors duration-[var(--duration-fast)] ease-[var(--ease-drawer)] hover:bg-muted/40 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
            data-slot="toast-open"
            onClick={open.onClick}
            type="button"
          >
            {content}
          </button>
        ) : (
          content
        )}

        {repeatCount < 2 ? null : (
          <div
            className="flex w-9 shrink-0 justify-center self-center"
            data-slot="toast-repeat-count"
          >
            {repeatCount > 1 ? (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-subtitle-2xs whitespace-nowrap text-muted-foreground tabular-nums">
                <span aria-hidden>{t('repeatCount', { count: repeatCount })}</span>
                <span className="sr-only">{t('repeatCountLabel', { count: repeatCount })}</span>
              </span>
            ) : null}
          </div>
        )}

        {onDismiss ? (
          <button
            aria-label={t('dismiss')}
            className="relative shrink-0 self-start text-icon-tertiary transition-colors after:absolute after:inset-[-10px] hover:text-icon-primary"
            onClick={handleDismissClick}
            onPointerDown={handleDismissPointerDown}
            type="button"
          >
            <X aria-hidden className="pointer-events-none size-3.5" />
          </button>
        ) : null}
      </div>
      {actionContent ? <div className="flex flex-wrap gap-2 ps-6">{actionContent}</div> : null}
    </div>
  )
}
